import { useState, useCallback } from 'react';
import listen from 'app/home/general-components/Speech/listen';
import { v4 as uuidv4 } from 'uuid';

import packageJson from '../../../package.json';

export const AIChatStatus = {
    idle: 1,
    listening: 2,
    thinking: 3,
    errorNotAllowed: 4,
};

export default function useAIChat(sendEvent) {
    const [status, setStatus] = useState(AIChatStatus.idle);
    // const [speechGeneration, setSpeechGeneration] = useState(true);
    const [messages, setMessages] = useState([]);
    const [stats, setStats] = useState({});
    const [conversationId, setConversationId] = useState(uuidv4());

        const isStaging = (packageJson.serverName === "staging");
    const speechGeneration = isStaging

    const startListening = useCallback((language) => {
        setStatus(AIChatStatus.listening);
        let config = {};
        if (language === 'fa') {
            config = { lang: "fa-IR" };
        }
        listen(config).then(
            transcript => {
                console.log('You said: ' + (transcript || 'nothing'));
                if (transcript) {
                    setStatus(AIChatStatus.thinking);
                    setMessages(l => [...l, {
                        // no index. the message with no index will be removed on next update
                        origin: "user",
                        text: transcript,
                    }]);
                    sendEvent('ai_text_prompt', {
                        "conv_id": conversationId,
                        "prompt_choices": [transcript],
                        "generate_speech": speechGeneration
                    });
                } else {
                    setStatus(AIChatStatus.idle);
                }
            },
            event => { // error
                console.error(event);
                if (event && (
                    (event.error === "service-not-allowed") ||
                    (event.error === "not-allowed")
                )) {
                    setStatus(AIChatStatus.errorNotAllowed);
                } else {
                    setStatus(AIChatStatus.idle);
                }
            }
        );
    }, [sendEvent, conversationId, speechGeneration]);

    const addNewMessage = useCallback((message, language) => {
        setStatus(AIChatStatus.thinking);
        setMessages(l => [...l, {
            // no index. the message with no index will be removed on next update
            origin: "user",
            text: message,
        }]);
        sendEvent('ai_text_prompt', {
            "conv_id": conversationId,
            "prompt_choices": [message],
            "generate_speech": speechGeneration
        });
    }, [sendEvent, conversationId, speechGeneration]);

    const clearChat = useCallback(() => {
        setConversationId(uuidv4());
        setMessages([]);
        setStats({});
        setStatus(s => s !== AIChatStatus.listening ? AIChatStatus.idle : s);
    }, []);

    const onAIHistory = useCallback(({ msg }) => {
        if (msg.conv_id === undefined) {
            setConversationId(uuidv4());
            setMessages([]);
            setStats({});
            setStatus(s => s !== AIChatStatus.listening ? AIChatStatus.idle : s);
        } else {
            if (conversationId !== msg.conv_id) {
                setConversationId(msg.conv_id);
                setMessages(msg.history);
                setStats(msg.stats ?? {});
                const lastMessageIsUser = msg.history[msg.history.length - 1].origin === "user";
                setStatus(s =>
                    s !== AIChatStatus.listening ?
                        (lastMessageIsUser ? AIChatStatus.thinking : AIChatStatus.idle)
                        : s);
            } else {
                if (msg.stats) {
                    setStats(msg.stats);
                }
                if (msg.history.length > 0) { // (conversationId === msg.conv_id), merge history
                    let firstIncomming = msg.history[0].index;
                    let newMessages = [];
                    for (let i = 0; i < messages.length; i++) {
                        const m = messages[i];
                        if (m.index < firstIncomming) {
                            newMessages.push(m); // exsisting messages
                        } else {
                            break;
                        }
                    }
                    for (let i = 0; i < msg.history.length; i++) {
                        const m = msg.history[i];
                        newMessages.push(m); // new messages
                    }
                    setMessages(newMessages);
                    const lastMessageIsUser = newMessages[newMessages.length - 1].origin === "user";
                    setStatus(s =>
                        s !== AIChatStatus.listening ?
                            (lastMessageIsUser ? AIChatStatus.thinking : AIChatStatus.idle)
                            : s);
                } else {
                    console.log("onAIHistory: msg.history.length == 0, skipping.", msg.history);
                }
            }
        }
    }, [conversationId, messages])

    return { status, messages, startListening, addNewMessage, clearChat, onAIHistory, stats };
}

// listen({ lang: "fa-IR" }).then(
//     transcript => {
//         console.log('You said: ' + (transcript || 'nothing'));
//     },
//     console.error
// );
